"use client";

import { useTranslations } from "next-intl";
import { QuestionRenderer } from "@/components/questions/QuestionRenderer/QuestionRenderer";
import { useAssessment } from "@/contexts/AssessmentContext/AssessmentContext";
import { PageState } from "@/contexts/AssessmentContext/AssessmentContext.types";
import { QuestionType } from "@/commonTypes/question";
import { getAssetUrl } from "@/utils/assetUrl";
import PollingScreen from "../PollingScreen/PollingScreen";
import { useEffect, useRef } from "react";
import ResultsPage from "../ResultsPage/ResultsPage";
import ErrorScreen from "../ErrorScreen/ErrorScreen";
import Header from "@/components/Header/Header";
import { useVariant, PAGE_VARIANT } from "@/hooks/useVariant";
import {
  pushPageContext,
  fireStepView,
  formatExperimentVariants,
} from "@/utils/analytics";
import { setQuizStartTimestamp } from "@/utils/sessionStorage";
import { preloadTransitionImages } from "@/utils/transitionImagePreload";
import { usePageViewTracking } from "@/hooks/usePageViewTracking";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinnerThird } from "@awesome.me/kit-c10ef98ed3/icons/duotone/solid";

function shouldSkipPreloadOnConnection(): boolean {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string };
    }
  ).connection;
  const isSaveData = connection?.saveData === true;
  const isSlowConnection = ["slow-2g", "2g", "3g"].includes(
    connection?.effectiveType ?? "",
  );

  return isSaveData || isSlowConnection;
}

export function QuestionnaireLayout() {
  const tQuestionnaire = useTranslations("questionnaire");
  const {
    currentQuestion,
    questions,
    visibleQuestions,
    isLastQuestion,
    isQuizStarted,
    pageState,
    isLoading,
    error,
    experimentAssignments,
  } = useAssessment();
  const variant = useVariant();
  const pageVariant = PAGE_VARIANT[variant];

  usePageViewTracking();

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);

  // Start quiz duration timer once on mount
  useEffect(() => {
    setQuizStartTimestamp();
  }, []);

  // Push the sa-entry page context once, as soon as the questionnaire load
  // resolves (questions arrived, or the fetch failed). It is a landing event:
  // pushing earlier duplicates it when experiment assignments land, and
  // re-pushing later rewinds the context to the landing page mid-quiz when
  // sex-at-birth flips pageVariant to "women".
  const experimentVariant = formatExperimentVariants(experimentAssignments);
  const hasResolvedLoad = questions.length > 0 || Boolean(error);
  const hasPushedEntryRef = useRef(false);
  useEffect(() => {
    if (!hasResolvedLoad || hasPushedEntryRef.current) return;
    hasPushedEntryRef.current = true;
    pushPageContext("sa-entry", pageVariant, experimentVariant);
  }, [hasResolvedLoad, pageVariant, experimentVariant]);

  // Fire step view event on question change
  const prevQuestionIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentQuestion) return;
    if (currentQuestion.questionId === prevQuestionIdRef.current) return;
    prevQuestionIdRef.current = currentQuestion.questionId;
    fireStepView(currentQuestion, visibleQuestions, pageVariant);
  }, [currentQuestion, visibleQuestions, pageVariant]);

  // Preload the polling screen background video when the user reaches the last question
  useEffect(() => {
    if (!isLastQuestion) return;

    // Don't preload if the video won't play or the user is on a slow connection
    if (prefersReducedMotion || shouldSkipPreloadOnConnection()) return;

    // Create a video element that doesn't exist in the DOM to preload the video
    const video = document.createElement("video");
    video.preload = "auto";
    video.src = getAssetUrl("/images/polling_screen_background_video.mp4");
  }, [isLastQuestion, prefersReducedMotion]);

  // Preload transition images as soon as the user starts the quiz.
  useEffect(() => {
    if (!isQuizStarted) return;

    if (shouldSkipPreloadOnConnection()) return;

    preloadTransitionImages(questions);
  }, [isQuizStarted, questions]);

  return (
    <>
      {pageState !== PageState.POLLING && pageState !== PageState.ERROR && <Header />}
      {pageState === PageState.QUESTIONNAIRE && (
        <div
          className={`page-container${
            currentQuestion?.questionType === QuestionType.TRANSITION
              ? " page-container--transition"
              : ""
          }`}
        >
          {isLoading && (
            <div className="questionnaire-loading-state">
              <div className="questionnaire-loading-state-spinner">
                <FontAwesomeIcon
                  icon={faSpinnerThird}
                  className="questionnaire-loading-state-icon"
                  aria-label={tQuestionnaire("loading")}
                />
              </div>
            </div>
          )}
          {error && (
            <div className="questionnaire-error-state">
              <p>{error}</p>
            </div>
          )}
          {!isLoading && !error && currentQuestion && <QuestionRenderer />}
        </div>
      )}
      {pageState === PageState.POLLING && <PollingScreen />}
      {pageState === PageState.ERROR && <ErrorScreen />}
      {pageState === PageState.RESULTS && <ResultsPage />}
    </>
  );
}
